import { Space, Tag, Tooltip, Typography } from 'antd'
import { Fragment, type ReactNode } from 'react'

import { useStyles } from './styles'

const { Text } = Typography

export type LabeledTagGridItem = {
  key: string
  label: string
  /** Hover text — usually the machine-readable value the label stands in for. */
  tooltip?: string
}

export type LabeledTagGridRow = {
  key: string
  label: string
  items: LabeledTagGridItem[]
}

type Props = {
  rows: LabeledTagGridRow[]
  /** Secondary line above the grid, e.g. "22 permissions across 6 resources". */
  summary?: ReactNode
}

/**
 * A `label → tags` grid: labels in a fixed left column, tags wrapping in the right one, a rule
 * between rows.
 *
 * Extracted from the credential permission panel because the webhook detail page needed the same
 * reading pattern — one row per entity, chips for what it covers — and had reached for
 * `Descriptions`, which renders labels in the muted label colour, drops the rules and stretches
 * short values across the viewport.
 */
export const LabeledTagGrid = ({ rows, summary }: Props) => {
  const { styles } = useStyles()

  return (
    <div>
      {summary ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {summary}
        </Text>
      ) : null}
      <div className={styles.grid} style={{ marginTop: summary ? 10 : 0 }}>
        {rows.map((row, index) => {
          // The last row's rule would double up with the card border below it.
          const last = index === rows.length - 1
          const labelCls = last
            ? `${styles.rowLabel} ${styles.rowLast}`
            : styles.rowLabel
          const valueCls = last
            ? `${styles.rowValue} ${styles.rowLast}`
            : styles.rowValue

          return (
            <Fragment key={row.key}>
              {/* Tooltipped because the label column ellipsises rather than wraps. */}
              <Tooltip title={row.label}>
                <span className={labelCls}>{row.label}</span>
              </Tooltip>
              <div className={valueCls}>
                <Space size={[4, 4]} wrap>
                  {row.items.map((item) => (
                    <Tooltip key={item.key} title={item.tooltip}>
                      <Tag className={styles.itemTag}>{item.label}</Tag>
                    </Tooltip>
                  ))}
                </Space>
              </div>
            </Fragment>
          )
        })}
      </div>
    </div>
  )
}
